import config from '@common/config.js'
import { fail, success } from '@helpers/result-helper.js'
import router from '@router'
import msal from '@plugins/msal'
import toast from '@services/toasts/index.js'
import stringContainsAnySubstr from '@utils/string-contains-any-substr'
import $date from '@services/date/index.js'
import { isSuccess } from '@/helpers/http-status-helpers'

export default {
  namespaced: true,
  state: {
    // MSAL User
    account: getSavedState('auth.account'),
    // User Profile from DB
    currentUser: getSavedState('auth.currentUser'),
    redirectFrom: getSavedState('auth.redirectFrom'),
    accessToken: '', // Bearer token
    loadingCount: 0,
    auth: msal,
  },

  mutations: {
    SET_CURRENT_USER(state, newValue) {
      state.currentUser = { ...newValue, ...{ lastUpdated: $date() } }
      saveState('auth.currentUser', state.currentUser)
    },
    SET_ACCOUNT(state, newValue) {
      state.account = newValue
      saveState('auth.account', newValue)
    },
    SET_ACCESS_TOKEN(state, token) {
      state.accessToken = token
    },
    SET_USER_TO_UNAUTHENTICATED(state) {
      state.account = null
      saveState('auth.account', null)
      state.currentUser = null
      saveState('auth.currentUser', null)
      state.accessToken = null

      sessionStorage.clear()
    },
    SET_REDIRECT_FROM(state, url) {
      state.redirectFrom = url
      saveState('auth.redirectFrom', url)
    },
    START_LOADING(state) {
      state.loadingCount++
    },
    FINISH_LOADING(state) {
      state.loadingCount--
    },
  },

  getters: {
    currentUserDateFormat: (state) =>
      state.dateFormat ? state.dateFormat : 'DD-MM-YYYY',
    currentUserFullName: (state) =>
      state.currentUser ? state.currentUser.name : '',
    currentUserEmail: (state) =>
      state.currentUser ? state.currentUser.email : '',
    currentUser: (state) => state.currentUser,
    currentAccount: (state) => state.account,
    redirectFrom: (state) => state.redirectFrom,
    accessToken: (state) => state.accessToken,
    isUserLoggedIn: (state) => !!state.account, // && state.auth.isAuthenticated(),
    isLoadingAuth: (state) => state.loadingCount > 0,
    auth: (state) => state.auth,
    currentUserTeams: (state) => state.currentUser.teams,
    currentUserDivision: (state) => state.currentUser.division,
  },

  actions: {
    // This is automatically run in `src/state/store.js` when the app
    // starts, along with any other actions named `init` in other modules.
    init({ dispatch }) {
      // dispatch('refreshToken')
    },

    // Clears the redirectFrom value from store and returns it to avoid old redirectFrom data
    comsumeRedirectFrom({ commit, getters }) {
      const redirect = getters.redirectFrom
      commit('SET_REDIRECT_FROM', null)
      return redirect
    },

    async handleRedirect({ commit, dispatch, getters, state }, response) {
      const redirect = await dispatch('comsumeRedirectFrom')

      if (response !== null) {
        await dispatch('getUserFromAuthProvider')
      }

      // In case multiple accounts exist, you can select
      const currentAccounts = getters.auth.getAllAccounts()
      if (currentAccounts.length !== 0) {
        // TODO: Add choose account code here
        await dispatch('getUserFromAuthProvider')
      }

      // Using router.replace() here to stop the browser navigating to the
      // dashboard when the back button is pressed directly after a
      // successful login.
      if (redirect) router.replace(redirect)
    },

    // Logs in the current user.
    async logIn(
      { dispatch, getters, commit, rootGetters },
      redirectFrom = null
    ) {
      if (getters.isUserLoggedIn) return dispatch('refreshToken')

      // Maintain redirectFrom URL after redirect login
      commit('SET_REDIRECT_FROM', redirectFrom)

      const loginRequest = {
        scopes: ['openid'],
      }

      const errorHandler = (error) => {
        console.error(error)
        commit('SET_USER_TO_UNAUTHENTICATED')

        const errorCode = error.errorCode

        const noNotificationReq = ['user_cancelled']

        // Filter through errors that don't require a notifiction
        if (stringContainsAnySubstr(noNotificationReq, errorCode))
          return fail(error)

        toast.error('Oops.. Something went wrong')
        return fail(error)
      }

      if (rootGetters.isEmbedded) {
        getters.auth
          .loginPopup(loginRequest)
          .then(async (response) => {
            return await dispatch('handleRedirect', response)
          })
          .catch(errorHandler)
      } else {
        getters.auth.loginRedirect(loginRequest).catch(errorHandler)
      }
    },

    // Logs out the current user.
    async logOut({ commit, getters }) {
      commit('START_LOADING')
      return await getters.auth
        .logout({})
        .catch((error) => {
          toast.error('Oops.. Something went wrong trying to sign you out')
          return fail(error)
        })
        .finally(() => {
          commit('FINISH_LOADING')
        })
    },

    // Retrieves user account from auth provider
    getUserFromAuthProvider({ commit, getters }) {
      if (!getters.auth) return Promise.resolve(null)

      try {
        const myAccounts = getters.auth.getAllAccounts()
        commit('SET_ACCOUNT', myAccounts[0])
      } catch {
        commit('SET_ACCOUNT', null)
      }
    },

    // Validates the current user's token and refreshes it
    // with new data from the API.
    async refreshToken({ dispatch, commit, state, getters }) {
      if (!getters.auth) return Promise.resolve(fail()) // Prevents trying to access auth object before it is initialised
      dispatch('getUserFromAuthProvider')
      if (!state.account) return Promise.resolve(fail())

      commit('START_LOADING')

      const request = {
        scopes: [config.get('scopes.openId'), config.get('scopes.read')],
        account: state.account,
      }

      let response

      try {
        response = await getters.auth.acquireTokenSilent(request)
        commit('SET_ACCESS_TOKEN', response.accessToken)
        return success()
      } catch (error) {
        console.warn('Silent token acquisition failed. Using interactive mode')
        return await getters.auth
          .acquireTokenPopup(request)
          .then((response) => {
            commit('SET_ACCESS_TOKEN', response.accessToken)
            return success()
          })
          .catch(() => {
            toast.error('Failed to authenticate')
            return fail()
          })
      } finally {
        commit('FINISH_LOADING')
      }
    },

    async getCurrentUserProfile({ commit }) {
      commit('START_LOADING')

      try {
        const response = await this.$api.me.get()

        if (isSuccess(response.status)) {
          commit('SET_CURRENT_USER', response.data)
          return success()
        }
      } catch (ex) {
        toast.show("Couldn't load user profile")
        return fail(ex.response)
      } finally {
        commit('FINISH_LOADING')
      }
    },

    clear({ commit }) {
      commit('SET_USER_TO_UNAUTHENTICATED')
    },
  },
}

// ===
// Private helpers
// ===

// eslint-disable-next-line no-unused-vars
function getSavedState(key) {
  const item = window.localStorage.getItem(key)
  return item && item !== 'undefined' ? JSON.parse(item) : ''
}

// eslint-disable-next-line no-unused-vars
function saveState(key, state) {
  window.localStorage.setItem(key, JSON.stringify(state))
}

// function setDefaultAuthHeaders(state) {
//   axios.defaults.headers.common.Authorization = state.currentUser
//     ? state.currentUser.token
//     : ''
// }
